import axios from 'axios'
import type {
  FileRecordProperty,
  UUID
} from '@indigohive/cogfy-types'
import { useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { validFile } from '../../../../helpers'
import { useCogfy, useToasts } from '../../../../hooks'
import { useTranslation } from 'react-i18next'
import { TrashIcon, UploadIcon } from 'lucide-react'
import clsx from 'clsx'
import { Button, ConfirmDeleteDialog, PreviewFileDialog } from '../../../../components'
import { FileRecordPropertyInputProps } from './FileRecordPropertyInput'

function hasPreview (type: string): boolean {
  return Boolean(type.match('image|video'))
}

const byteFormatter = new Intl.NumberFormat(
  'en-US',
  { style: 'unit', unit: 'byte', unitDisplay: 'narrow', notation: 'compact' }
)

export function FileRecordPropertyInputDefaultVariant (props: FileRecordPropertyInputProps) {
  const { collectionId, field, record, canEdit, disabled } = props

  const cogfy = useCogfy()
  const { t } = useTranslation()
  const toast = useToasts()
  const [fileToDeleteId, setFileToDeleteId] = useState<UUID | null>(null)
  const [loadingProgress, setLoadingProgress] = useState<number | null>(null)
  const [previewOpen, setPreviewOpen] = useState<boolean>(false)
  const [imageDimensions, setImageDimensions] = useState({ height: 0, width: 0 })
  const imageRef = useRef<HTMLImageElement>(null)

  const fileResult = (record.properties[field.id] as FileRecordProperty).file?.value
  const isFileUploading = fileResult?.uploadStatus === 'pending'
  const isImage = fileResult?.type.match('image')

  const uploadFile = useMutation({
    mutationFn: async (file: File) => {
      const error = validFile(field.data, file)

      if (error) return toast.error(t(error))

      const result = await cogfy.uploadFile({
        collectionId,
        fieldId: field.id,
        recordId: record.id,
        type: file.type,
        size: file.size,
        name: file.name
      })

      await axios.post(
        result.signedUrl,
        { ...result.fields, file },
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (event) => {
            if (event.total) {
              const progress = Math.ceil(Math.round(event.loaded * 100) / event.total)
              setLoadingProgress(progress)
            }
          }
        }
      )

      await cogfy.completeUploadFile({ fileId: result.id })

      setLoadingProgress(null)
    },
    onError: () => toast.error(t('Error on uploading document'))
  })

  const deleteFile = useMutation({
    mutationFn: async (id: UUID) => {
      await cogfy.deleteFile({ fileId: id })
    }
  })

  useEffect(() => {
    if (!imageRef.current) return
    const img = imageRef.current

    const handleLoadImage = () => {
      setImageDimensions({
        height: img.naturalHeight,
        width: img.naturalWidth
      })
    }

    img.complete
      ? handleLoadImage()
      : img.addEventListener('load', handleLoadImage)

    return () => {
      img.removeEventListener('load', handleLoadImage)
    }
  }, [imageRef.current, fileResult])

  return (
    <div>
      {isFileUploading && (
        <div className="flex">
          <span className="loading loading-spinner loading-xs"></span>
          {loadingProgress !== null && <span className="text-slate-500 ml-2">{loadingProgress} %</span>}
        </div>
      )}

      {!fileResult && !isFileUploading && (
        <label className="flex cursor-pointer items-center text-slate-500 hover:text-slate-800">
          <input
            disabled={disabled}
            type="file"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) {
                uploadFile.mutate(file)
              }
            }}
          />
          <UploadIcon size={16} className="ml-1 mr-2" />
          Upload
        </label>
      )}

      {fileResult && !isFileUploading && (
        <div className="flex items-center gap-2 group">
          {hasPreview(fileResult.type) &&
            <div
              className="flex items-center gap-2 hover:bg-slate-50 rounded-md p-1"
              onClick={() => setPreviewOpen(true)}
            >
              {isImage && <img ref={imageRef} src={`${fileResult.url}`} className="h-8 object-cover aspect-square rounded-xs" />}
              <span
                className={clsx(
                  'flex',
                  'gap-1',
                  'items-center'
                )}
              >
                {fileResult.name}
                <span className='text-xs text-slate-400'>
                  ({imageDimensions?.height}x{imageDimensions?.width}), {byteFormatter.format(fileResult.size)}
                </span>
              </span>
            </div>
          }
          {!hasPreview(fileResult.type) &&
            <a
              href={`${fileResult.url}`}
              target="_blank"
              rel="noreferrer"
              className='hover:underline'
            >
              {fileResult.name}
            </a>
          }
          {canEdit && (
            <Button
              onClick={() => setFileToDeleteId(fileResult.id)}
              className={clsx(
                'opacity-0',
                'group-hover:opacity-100',
                'transition-opacity',
                'duration-300'
              )}
              ghost
              square
              size="xs"
            >
              <TrashIcon size={16} />
            </Button>
          )}
        </div>
      )}

      {fileToDeleteId && (
        <ConfirmDeleteDialog
          title={`${t('Delete file')} '${fileResult?.name}'?`}
          open={fileToDeleteId !== null}
          onConfirm={() => {
            if (fileToDeleteId) {
              deleteFile.mutate(fileToDeleteId)
              setFileToDeleteId(null)
            }
          }}
          onClose={() => {
            setFileToDeleteId(null)
          }}
        />
      )}

      {previewOpen && fileResult &&
        <PreviewFileDialog
          filename={fileResult.name}
          fileUrl={fileResult.url}
          fileType={fileResult.type}
          fileSize={fileResult.size}
          imageDimensions={imageDimensions}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      }
    </div>
  )
}
